import type { VercelKV } from '@vercel/kv';

import { KVMetricsTracker } from './kv-metrics-tracker';
import {
  BaseRangeOptions,
  KVValue,
  RemoteClient, VercelLexOptions, VercelRangeByScoreOptions, ZSetResponse
} from './types';


export class VercelKVAdapter extends KVMetricsTracker implements RemoteClient {
  constructor(private client: VercelKV) {
    super();
  }

  async get<T>(key: string): Promise<KVValue<T>> {
    this.trackOperation('get', key);
    return this.client.get<T>(key);
  }

  async set(key: string, value: unknown): Promise<void> {
    this.trackOperation('set', key);
    await this.client.set(key, value);
  }

  async del(key: string): Promise<void> {
    this.trackOperation('del', key);
    await this.client.del(key);
  }

  async zadd(
    key: string,
    data: { score: number; member: unknown }
  ): Promise<void> {
    this.trackOperation('zadd', key);
    await this.client.zadd(key, {
      score: data.score,
      member: data.member
    });
  }

  async zrem(key: string, member: unknown): Promise<void> {
    this.trackOperation('zrem', key);
    await this.client.zrem(key, member);
  }

  async zremrangebyscore(
    key: string,
    min: number,
    max: number
  ): Promise<void> {
    this.trackOperation('zremrangebyscore', key);
    await this.client.zremrangebyscore(key, min, max);
  }

  async zrange<T>(
    key: string,
    start: number,
    end: number,
    options?: BaseRangeOptions
  ): Promise<ZSetResponse<T>[]> {
    this.trackOperation('zrange', key);

    const result = await this.client.zrange(key, start, end, options);
    return this.formatZrangeResponse<T>(result, options?.withScores ?? false);

  }

  async zrangeByScore<T>(
    key: string,
    min: number | '-inf' | '+inf' | `(${number}`,
    max: number | '-inf' | '+inf' | `(${number}`,
    options?: BaseRangeOptions
  ): Promise<ZSetResponse<T>[]> {
    this.trackOperation('zrangeByScore', key);
    const vercelOptions: VercelRangeByScoreOptions = {
      ...options,
      byScore: true
    };

    const result = await this.client.zrange(key, min, max, vercelOptions);
    return this.formatZrangeResponse<T>(result, options?.withScores ?? false);

  }

  async zrangeByLex<T>(
    key: string,
    min: `(${string}` | `[${string}` | '-' | '+',
    max: `(${string}` | `[${string}` | '-' | '+',
    options?: BaseRangeOptions
  ): Promise<ZSetResponse<T>[]> {
    this.trackOperation('zrangeByLex', key);
    const vercelOptions: VercelLexOptions = {
      ...options,
      byLex: true
    };

    const result = await this.client.zrange(key, min, max, vercelOptions);
    return this.formatZrangeResponse<T>(result, options?.withScores ?? false);

  }

  private formatZrangeResponse<T>(result:unknown[], withScores:boolean): ZSetResponse<T>[] {
    if (withScores) {
      // Handle the alternating array of [member, score, member, score, ...]
      const responses: ZSetResponse<T>[] = [];
      for (let i = 0; i < result.length; i += 2) {
        responses.push({
          member: result[i] as T,
          score: Number(result[i + 1])
        });
      }
      return responses;
    } else {
      // Handle array of just members
      return result.map((member) => ({
        member: member as T
      }));
    }
  }

  async scan(
    cursor: number,
    options?: {
      match?: string;
      count?: number
    }
  ): Promise<[number, string[]]> {
    this.trackOperation('scan', options?.match ?? '*');

    try {
      const result = await this.client.scan(cursor, {
        match: options?.match,
        count: options?.count
      });

      if (Array.isArray(result) && result.length === 2) {
        return [
          Number(result[0]),
          result[1]
        ];
      }

      console.warn('Unexpected scan response format:', result);
      return [0, []];

    } catch (error) {
      console.error('Scan operation failed:', error);
      throw error;
    }
  }
}